// CustomPagination.tsx
import React from "react";
import { Box, Typography, Pagination, Select, MenuItem } from "@mui/material";

type PaginationBase = {
  pageIndex: number;
  pageSize: number;
};

interface CustomPaginationProps<T extends PaginationBase> {
  param: T;
  setParam: React.Dispatch<React.SetStateAction<T>>;
  totalCount: number;
}

export default function CustomPagination<T extends PaginationBase>({
  param,
  setParam,
  totalCount,
}: CustomPaginationProps<T>) {
  return (
    <Box
      display="flex"
      justifyContent="flex-end"
      alignItems="center"
      padding="20px 0px 20px 0px"
      gap={1}
    >
      <Typography
        variant="body2"
        sx={{ color: "#919191 !important", fontSize: "12px" }}
      >
        {`${(param.pageIndex - 1) * param.pageSize + 1} - ${Math.min(
          param.pageIndex * param.pageSize,
          totalCount
        )} of ${totalCount}`}{" "}
        results shown
      </Typography>

      <Pagination
        page={param.pageIndex}
        count={Math.ceil(totalCount / param.pageSize)}
        onChange={(e, value) =>
          setParam((prev) => ({ ...prev, pageIndex: value }))
        }
        siblingCount={1}
        boundaryCount={1}
        color="primary"
        sx={{
          "& .MuiPaginationItem-root": {
            borderRadius: "2px",
            padding: "10px !important",
            color: "#919191 !important",
            backgroundColor: "white !important",
            border: "1px solid #919191",
            minWidth: "0 !important",
            height: "0 !important",
          },
          "& .MuiPaginationItem-ellipsis": {
            border: "none !important",
            background: "transparent !important",
            padding: "0px !important",
            minWidth: "auto !important",
            height: "auto !important",
            borderRadius: "2px",
          },
          "& .Mui-selected": {
            borderRadius: "2px",
            color: "white !important",
            backgroundColor: "#232323 !important",
            border: "1px solid #232323 !important",
          },
        }}
      />

      <Select
        value={param.pageSize}
        onChange={(e) =>
          setParam((prev) => ({
            ...prev,
            pageSize: Number(e.target.value),
            pageIndex: 1,
          }))
        }
        size="small"
        sx={{
          backgroundColor: "white",
          border: "1px solid #919191",
          color: "#919191 !important",
          borderRadius: "2px",
          fontSize: "14px",
          "& .MuiSelect-select": {
            padding: "0px 0px",
            paddingLeft: "6px",
          },
          "& fieldset": {
            border: "none",
            color: "#919191 !important",
          },
        }}
      >
        {[5, 10, 20, 50, 100].map((size) => (
          <MenuItem key={size} value={size} className="label-item-button">
            {size} / page
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}
